import React, { useMemo, useState } from "react";
import { X, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface TagsInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  className?: string;
  suggestions?: string[];
  showSuggestions?: boolean;
  onQueryChange?: (value: string) => void;
  disabled?: boolean;
}

const TagsInput: React.FC<TagsInputProps> = ({
  value,
  onChange,
  placeholder = "Digite e pressione Enter...",
  className,
  suggestions = [],
  showSuggestions = true,
  onQueryChange,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState("");

  const normalizedValue = useMemo(
    () => value.map((item) => item.trim()).filter(Boolean),
    [value],
  );

  const addTag = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    if (normalizedValue.includes(trimmed)) {
      setInputValue("");
      return;
    }
    onChange([...normalizedValue, trimmed]);
    setInputValue("");
  };

  const removeTag = (tag: string) => {
    onChange(normalizedValue.filter((item) => item !== tag));
  };

  const clearAll = () => {
    onChange([]);
    setInputValue("");
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      addTag(inputValue);
    }
  };

  const filteredSuggestions = useMemo(() => {
    if (!showSuggestions || !inputValue.trim()) return [];
    const lower = inputValue.trim().toLowerCase();
    return suggestions
      .filter((item) => item.toLowerCase().includes(lower))
      .filter((item) => !normalizedValue.includes(item))
      .slice(0, 6);
  }, [inputValue, normalizedValue, showSuggestions, suggestions]);

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex flex-wrap gap-2 min-h-[40px] rounded-md border bg-background p-2 focus-within:ring-1 focus-within:ring-ring">
        {normalizedValue.map((tag) => (
          <Badge
            key={tag}
            variant="secondary"
            className="flex items-center gap-1"
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              className="rounded-full p-0.5 transition-colors hover:bg-muted"
              aria-label={`Remover ${tag}`}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}

        <Input
          type="text"
          value={inputValue}
          onChange={(event) => {
            const next = event.target.value;
            setInputValue(next);
            onQueryChange?.(next);
          }}
          onKeyDown={handleKeyDown}
          placeholder={
            normalizedValue.length === 0 ? placeholder : "Adicionar termo..."
          }
          className="h-7 min-w-[140px] flex-1 border-0 p-0 shadow-none focus-visible:ring-0"
          disabled={disabled}
        />
      </div>

      {filteredSuggestions.length > 0 && (
        <div className="rounded-md border bg-background p-2 text-xs">
          <div className="mb-2 text-muted-foreground">Sugestões:</div>
          <div className="flex flex-wrap gap-2">
            {filteredSuggestions.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => addTag(item)}
                className="rounded-full border px-2 py-1 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-foreground"
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {normalizedValue.length === 0
            ? "Nenhum termo configurado."
            : `${normalizedValue.length} termo${normalizedValue.length > 1 ? "s" : ""} configurado${normalizedValue.length > 1 ? "s" : ""}`}
        </span>
        <div className="flex gap-2">
          {inputValue.trim() && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 px-2"
              onClick={() => addTag(inputValue)}
            >
              <Plus className="mr-1 h-3 w-3" />
              Adicionar
            </Button>
          )}
          {normalizedValue.length > 0 && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-muted-foreground hover:text-destructive"
              onClick={clearAll}
            >
              Limpar todos
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TagsInput;
